import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Login from '../Auth/Login';
import Register from '../Auth/Register';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { PUBLIC_DISCOVERY_PAGES } from './PublicDiscoveryPage';
import SeoMeta from '../Shared/SeoMeta';
import styles from './Landing.module.css';

const RACE_AVATARS = [
  { src: '/img/avatars/human.male.1.png',  alt: 'Human' },
  { src: '/img/avatars/elf.female.1.png',  alt: 'Elf' },
  { src: '/img/avatars/dwarf.male.1.png',  alt: 'Dwarf' },
  { src: '/img/avatars/orc.male.1.png',    alt: 'Orc' },
  { src: '/img/avatars/troll.female.1.png',alt: 'Troll' },
  { src: '/img/avatars/goblin.male.1.png', alt: 'Goblin' },
];

const FEATURES = [
  {
    icon: '⚔️',
    title: 'Arena Combat',
    desc: 'Clash with mercenaries across the realm in brutal turn-based battles.',
  },
  {
    icon: '🛡️',
    title: 'Guild Wars',
    desc: 'Build/join a guild, fight for rankings, and dominate rival guilds.',
  },
  {
    icon: '🏆',
    title: 'Tournaments',
    desc: 'Enter tournaments for glory, rare loot, and see who will be crowned a legend.',
  },
  {
    icon: '💰',
    title: 'Player Economy',
    desc: 'Trade, buy, and sell items in the Auction House.',
  },
];

const SEARCH_INTENT_PILLARS = PUBLIC_DISCOVERY_PAGES.map((page) => ({
  title: page.cardTitle,
  desc: page.cardDescription,
  to: page.path,
}));

const FAQS = [
  {
    question: 'Is Njordheim a text-based game?',
    answer: 'Yes. Njordheim Arena is a browser-based text-based RPG built around readable combat logs, mercenary management, gear progression, and fantasy arena battles.',
  },
  {
    question: 'What makes Njordheim an arena game?',
    answer: 'The core of the game is competitive arena combat: mercenaries fight in turn-based battles, ranked matches, guild conflicts, and seasonal tournaments.',
  },
  {
    question: 'Why is it a mercenary game?',
    answer: 'You recruit, train, equip, and direct mercenaries through battles, professions, crafting, beasts, and long-term progression decisions.',
  },
  {
    question: 'Do I need to download anything to play?',
    answer: 'No. Njordheim runs in your browser, so you can create an account and start building your mercenaries without installing a separate client.',
  },
];

const LANDING_TITLE = 'Njordheim Arena | Text-Based Arena RPG and Mercenary Browser Game';

const LANDING_DESCRIPTION = 'Njordheim Arena is a browser-based text-based arena RPG where you build mercenaries, fight turn-based battles, join guilds, and climb tournament rankings.';

const LANDING_VIDEO_GAME_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'VideoGame',
  name: 'Njordheim Arena',
  url: 'https://njordheim.com/',
  description: LANDING_DESCRIPTION,
  applicationCategory: 'GameApplication',
  gamePlatform: 'Web Browser',
  operatingSystem: 'Any',
  playMode: 'MultiPlayer',
  genre: ['Text-based RPG', 'Arena game', 'Mercenary game', 'Fantasy RPG'],
  inLanguage: 'en',
};

const LANDING_FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: answer,
    },
  })),
};

const DiscordIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.033.055a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
  </svg>
);

const Landing: React.FC = () => {
  const [isRegistering, setIsRegistering] = useState(false);
  const authRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    // If the user already has an active session, skip the landing page
    // and go straight to /home. Socket creation is handled by SocketContext.
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL || ''}/api/auth/user`, { withCredentials: true })
      .then((response) => {
        setUser(response.data);
        navigate('/home');
      })
      .catch((error) => console.log('User not authenticated:', error));
  }, [setUser, navigate]);

  const scrollToAuth = (register = false) => {
    setIsRegistering(register);
    setTimeout(() => authRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };

  return (
    <div className={styles.landingPage}>
      <SeoMeta
        title={LANDING_TITLE}
        description={LANDING_DESCRIPTION}
        path="/"
        jsonLd={[LANDING_VIDEO_GAME_SCHEMA, LANDING_FAQ_SCHEMA]}
      />

      {/* ── Header ──────────────────────────── */}
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.logo}>
            <span className={styles.logoRune}>⚔</span>
            <span className={styles.logoTitle}>NJORDHEIM</span>
          </div>
          <div className={styles.headerActions}>
            <button
              className={styles.loginPill}
              onClick={() => scrollToAuth(false)}
            >
              Login
            </button>
            <a
              href="https://discord.gg/ZR3HDBX29C"
              target="_blank"
              rel="noreferrer"
              className={styles.discordPill}
            >
              <DiscordIcon size={16} />
              <span>Join Discord</span>
            </a>
          </div>
        </div>
      </header>

      {/* ── Hero ────────────────────────────── */}
      <section className={styles.hero}>
        <p className={styles.heroEyebrow}>Text-Based Arena RPG</p>
        <h1 className={styles.heroTitle}>NJORDHEIM ARENA<br />MERCENARY BROWSER RPG</h1>
        <div className={styles.heroDivider}>
          <span />
          <span className={styles.heroDividerRune}>✦</span>
          <span />
        </div>
        <p className={styles.heroSub}>Build mercenaries, fight turn-based arena battles, and rise through guild wars and fantasy tournaments.</p>

        {/* Race avatars */}
        <div className={styles.raceStrip}>
          {RACE_AVATARS.map((r) => (
            <img
              key={r.alt}
              src={r.src}
              alt={r.alt}
              className={styles.raceAvatar}
              title={r.alt}
            />
          ))}
        </div>
      </section>

      {/* ── Feature pillars ─────────────────── */}
      <section className={styles.features}>
        <div className={styles.featureGrid}>
          {FEATURES.map((f) => (
            <div key={f.title} className={styles.featurePillar}>
              <span className={styles.featureIcon}>{f.icon}</span>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.discovery}>
        <div className={styles.sectionIntro}>
          <p className={styles.sectionEyebrow}>Why Players Find Njordheim</p>
          <h2 className={styles.sectionTitle}>A browser-based text arena game built around mercenary strategy, readable combat, and long-term rivalry.</h2>
          <p className={styles.sectionLead}>
            Njordheim is made for players searching for text-based games with real progression. Instead of twitch combat,
            you win by training mercenaries, choosing equipment, building guild strength, and understanding how each battle unfolds.
          </p>
        </div>
        <div className={styles.discoveryGrid}>
          {SEARCH_INTENT_PILLARS.map((pillar) => (
            <article key={pillar.title} className={styles.discoveryCard}>
              <h3>{pillar.title}</h3>
              <p>{pillar.desc}</p>
              <Link to={pillar.to} className={styles.discoveryLink}>Read more</Link>
            </article>
          ))}
        </div>
      </section>

      {/* ── Main: lore + auth ───────────────── */}
      <main className={styles.main}>

        {/* Lore */}
        <div className={styles.lorePanel}>
          <div>
            <h2 className={styles.loreSectionTitle}>Welcome to the World of Njordheim</h2>
            <div className={styles.loreDivider} />
            <p className={styles.loreParagraph}>
              Njordheim is a browser-based text-based arena RPG set in a harsh fantasy realm where reputation is earned through mercenary combat.
              Within the towering walls of its capital, every duel, ranked battle, and tournament run is another chance to build your legend.
            </p>
          </div>
          <p className={styles.loreParagraph}>
            From humble beginnings, train a mercenary and face daunting foes in turn-based combat.
            Challenge beasts that roam the wilderness, fight other mercenary companies, and learn how gear,
            runes, and professions shape every match.
          </p>
          <p className={styles.loreParagraph}>
            Discover forgotten lore, master the ancient arts of battle, and forge your legacy in a
            realm where planning matters as much as strength. Choose your race, Human, Elf, Dwarf, Orc, Troll,
            or Goblin, each with unique traits that shape how your mercenary grows.
          </p>
          <p className={styles.loreParagraph}>
            Join a guild, climb the tournament rankings, hunt legendary beasts, and craft your
            equipment. If you are looking for a mercenary game with text-based battles and long-term rivalry,
            this is where your company starts.
          </p>
          <a
            href="https://discord.gg/ZR3HDBX29C"
            target="_blank"
            rel="noreferrer"
            className={styles.discordCta}
          >
            <DiscordIcon size={18} />
            Join our community on Discord
          </a>
        </div>

        {/* Auth card */}
        <div className={styles.authPanel} ref={authRef}>
          <div className={styles.authCard}>
            <p className={styles.authCardTitle}>
              {isRegistering ? 'Create Your Account' : 'Enter the Realm'}
            </p>

            {/* Toggle tabs */}
            <div className={styles.authToggleTabs}>
              <button
                className={`${styles.authTab} ${!isRegistering ? styles.authTabActive : ''}`}
                onClick={() => setIsRegistering(false)}
              >
                Login
              </button>
              <button
                className={`${styles.authTab} ${isRegistering ? styles.authTabActive : ''}`}
                onClick={() => setIsRegistering(true)}
              >
                Register
              </button>
            </div>

            {isRegistering ? <Register embedded /> : <Login embedded />}
          </div>
        </div>

      </main>

      <section className={styles.faqSection}>
        <div className={styles.sectionIntro}>
          <p className={styles.sectionEyebrow}>FAQ</p>
          <h2 className={styles.sectionTitle}>What kind of game is Njordheim Arena?</h2>
        </div>
        <div className={styles.faqGrid}>
          {FAQS.map((faq) => (
            <article key={faq.question} className={styles.faqCard}>
              <h3>{faq.question}</h3>
              <p>{faq.answer}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ── Footer ──────────────────────────── */}
      <footer className={styles.footer}>
        <p className={styles.footerText}>
          NJORDHEIM RPG &nbsp;·&nbsp; ALL RIGHTS RESERVED &nbsp;·&nbsp;{' '}
          <Link to="/privacy" className={styles.footerLink}>Privacy Policy</Link>
        </p>
      </footer>

    </div>
  );
};

export default Landing;

